import { recoverCustomerInvoiceBulkPeriods } from '@/utils/accounting-period-setup';
import qs from "qs";
import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';
import {
    CUSTOMER_INVOICE_LIST_TO_ITEMS,
    buildInvoiceItemsExportOrdering,
    mapInvoiceListFiltersToItemFilters,
} from '@/utils/invoice-items-export-filters';

const {
    getStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

/** Shared across all customer-invoice tabs (section constraints applied at query time). */
const CUSTOMERINVOICES_FILTERS_STORAGE_KEY = getStorageKey('customerinvoices');
const DEFAULT_USER_FILTERS = {
    offset: 0,
    limit: 20,
    ordering: "-invoice_date,billed_name",
};

const state = {
    section: null,
    invoices: [],
    invoices_count: null,
    /** User-chosen filters persisted across tabs (no section constraints). */
    invoices_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Effective filters bound to API (user + section overlay). */
    invoices_filters: { ...DEFAULT_USER_FILTERS },
    invoices_user_filters_hydrated: false,
    invoices_loading: false,
    invoices_loaded: false,
    invoices_loading_errors: null,
    invoices_cancel_source: null,

    billingaccounts: [],
    billingaccounts_loading: false,
    billingaccounts_loading_errors: null,

    invoices_stats: {},
    invoices_stats_loading: false,
    invoices_stats_loading_errors: null,
    invoices_stats_cancel_source: null,

    marking_as_paid: false,
    selected_items: [],
    bulk_action_loading: false,
};

const modeFilters = {
    draft: {
        status: "draft",
        issued: true,
        ordering: '-invoice_date,billed_name',
    },
    waitingdelivery: {
        status: "validated",
        sent: false,
        paid: false,
        issued: true,
        ordering: '-invoice_date,billed_name',
    },
    waitingpayment: {
        status: "validated",
        paid: false,
        issued: true,
        ordering: '-invoice_date,billed_name',
    },
    due: {
        status: "validated",
        paid: false,
        due: true,
        credit: false,
        issued: true,
        ordering: '-invoice_date,billed_name',
    },
    paid: {
        status: "validated",
        paid: true,
        issued: true,
        ordering: '-invoice_date,billed_name',
    },
    all: {
        issued: true,
        ordering: '-invoice_date,billed_name',
    },
};

let getSectionFilters = function (section) {
    return modeFilters[section] || {};
};

/** Session counter key for a customer-invoices tab — same keys as tab badges. */
const SECTION_STATS_KEYS = {
    all: 'invoices',
    draft: 'draft_customerinvoices',
    waitingdelivery: 'waiting_delivery_customerinvoices',
    waitingpayment: 'waitingpayment_customerinvoices',
    due: 'due_customerinvoices',
    paid: 'paid_customerinvoices',
};

const EMPTY_INVOICES_STATS = {
    invoices: 0,
    total_amount_excl_vat: 0,
    total_amount_incl_vat: 0,
};

const getSectionCustomerInvoicesCount = (stats, section) => {
    if (!stats) {
        return null;
    }
    const key = SECTION_STATS_KEYS[section];
    if (!key) {
        return null;
    }
    const count = stats[key];
    return count == null ? null : count;
};

const isCustomerInvoicesSectionKnownEmpty = (rootState, section) => {
    if (!rootState.session?.stats_loaded) {
        return false;
    }
    return getSectionCustomerInvoicesCount(rootState.session.stats, section) === 0;
};

const applyEmptyCustomerInvoicesState = (commit, state) => {
    if (state.invoices_cancel_source) {
        state.invoices_cancel_source.cancel("canceled");
        commit('updateInvoicesCancelSource', null);
    }
    if (state.invoices_stats_cancel_source) {
        state.invoices_stats_cancel_source.cancel("canceled");
        commit('updateInvoicesStatsCancelSource', null);
    }
    commit('updateInvoices', []);
    commit('updateInvoicesCount', 0);
    commit('updateInvoicesLoading', false);
    commit('updateInvoicesLoaded', true);
    commit('updateInvoicesLoadingErrors', null);
    commit('updateInvoicesStats', { ...EMPTY_INVOICES_STATS });
    commit('updateInvoicesStatsLoading', false);
};

function loadSharedCustomerInvoicesUserFilters(section) {
    const shared = loadFiltersFromStorage(
        CUSTOMERINVOICES_FILTERS_STORAGE_KEY,
        null,
    );
    if (shared) {
        return extractUserFiltersFromEffective(
            shared,
            getSectionFilters(section || 'all'),
            {},
        );
    }
    const legacy = loadFiltersFromStorage(
        getStorageKey('customerinvoices', section || 'all'),
        { ...DEFAULT_USER_FILTERS },
    );
    return extractUserFiltersFromEffective(
        legacy,
        getSectionFilters(section || 'all'),
        {},
    );
}

function persistCustomerInvoicesUserFilters(userFilters) {
    saveFiltersToStorage(CUSTOMERINVOICES_FILTERS_STORAGE_KEY, userFilters);
}

function rebuildCustomerInvoicesFilters(state, { resetOffset = false } = {}) {
    const section = state.section || 'all';
    const previousOffset = state.invoices_filters?.offset ?? 0;
    state.invoices_filters = mergeUserAndSectionFilters(
        state.invoices_user_filters,
        getSectionFilters(section),
    );
    state.invoices_filters.offset = resetOffset ? 0 : previousOffset;
}

const mutations = {
    updateInvoices(state, invoices) {
        state.invoices = invoices;
        state.invoices_loaded = true;
    },
    updateInvoicesCount(state, invoices_count) {
        state.invoices_count = invoices_count;
    },
    updateInvoicesLoading(state, loading) {
        state.invoices_loading = loading;
    },
    updateInvoicesLoaded(state, loaded) {
        state.invoices_loaded = loaded;
    },
    updateInvoicesLoadingErrors(state, errors) {
        state.invoices_loading_errors = errors;
    },

    updateInvoicesStats(state, invoices_stats) {
        state.invoices_stats = invoices_stats;
    },
    updateInvoicesStatsLoading(state, loading) {
        state.invoices_stats_loading = loading;
    },
    updateInvoicesStatsLoadingErrors(state, errors) {
        state.invoices_stats_loading_errors = errors;
    },
    updateInvoicesStatsCancelSource(state, source) {
        state.invoices_stats_cancel_source = source;
    },
    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items;
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
    updateInvoicesFilters(state, invoices_filters) {
        const section = state.section || 'all';
        state.invoices_user_filters = extractUserFiltersFromEffective(
            invoices_filters,
            getSectionFilters(section),
            state.invoices_user_filters,
        );
        state.invoices_user_filters_hydrated = true;
        persistCustomerInvoicesUserFilters(state.invoices_user_filters);
        state.invoices_filters = mergeUserAndSectionFilters(
            state.invoices_user_filters,
            getSectionFilters(section),
        );
        state.invoices_filters.offset = invoices_filters?.offset ?? 0;
    },
    updateInvoicesCancelSource(state, source) {
        state.invoices_cancel_source = source;
    },

    updateSection(state, section) {
        state.section = section;
        state.invoices = [];
        state.invoices_count = 0;
        state.invoices_loaded = false;

        if (!state.invoices_user_filters_hydrated) {
            state.invoices_user_filters = {
                ...DEFAULT_USER_FILTERS,
                ...loadSharedCustomerInvoicesUserFilters(section),
            };
            state.invoices_user_filters_hydrated = true;
            persistCustomerInvoicesUserFilters(state.invoices_user_filters);
        }
        rebuildCustomerInvoicesFilters(state, { resetOffset: true });
    },

    updateBillingAccounts(state, billingaccounts) {
        state.billingaccounts = billingaccounts;
        state.billingaccounts_loading = false;
        state.billingaccounts_loading_errors = null;
    },

    updateBillingAccountsLoading(state, billingaccounts_loading) {
        state.billingaccounts_loading = billingaccounts_loading;
    },

    updateBillingAccountsLoadingErrors(state, billingaccounts_loading_errors) {
        state.billingaccounts = [];
        state.billingaccounts_loading = false;
        state.billingaccounts_loading_errors = billingaccounts_loading_errors;
    },

    updateMarkingAsPaid(state, marking) {
        state.marking_as_paid = marking;
    },
};

const actions = {
    async changeSection({ commit, dispatch, state }, mode) {
        if (mode == state.section) {
            return;
        }
        commit("updateSection", mode);
        commit('updateSelectedItems', []);

        await dispatch("fetchInvoices");
        dispatch("session/fetchStats", null, { root: true });
    },

    async markInvoiceAsPaid({ commit, dispatch, state }, params) {
        try {
            commit("updateMarkingAsPaid", true);
            const response = await axios.post(
                `/api/customerinvoices/${params.invoice.id}/paid/`,
                { paid: true }
            );
            dispatch("session/fetchStats", null, { root: true });
            dispatch("billingstats/fetchAllStats", null, { root: true });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateMarkingAsPaid", false);
        }
    },

    exportInvoicesAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.invoices_filters);
            filters.offset = null;
        }
        filters.format = "csv";
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/customerinvoices/",
            params: filters,
        }, $utils.getFilename('customerInvoices'));
    },

    exportInvoiceItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = mapInvoiceListFiltersToItemFilters(
                state.invoices_filters,
                CUSTOMER_INVOICE_LIST_TO_ITEMS,
            );
        }
        filters.invoice_issued = true;
        filters.format = "csv";
        filters.limit = CSV_EXPORT_LIMIT;
        filters.ordering = buildInvoiceItemsExportOrdering(
            state.invoices_filters.ordering,
        );

        $utils.downloadFile({
            url: "/api/customerinvoiceitems/",
            params: filters,
        }, $utils.getFilename('customerInvoiceItems'));
    },

    async fetchInvoices({ commit, dispatch, state, rootState }, params) {
        if (isCustomerInvoicesSectionKnownEmpty(rootState, state.section)) {
            applyEmptyCustomerInvoicesState(commit, state);
            return;
        }

        commit("updateInvoicesLoading", true);
        commit("updateInvoicesLoadingErrors", null);

        if (state.invoices_cancel_source) {
            state.invoices_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit("updateInvoicesCancelSource", cancelSource);

        try {
            const response = await axios.get("/api/customerinvoices/", {
                params: state.invoices_filters,
                cancelToken: state.invoices_cancel_source?.token,
            });
            commit("updateInvoices", response.data.results);
            commit("updateInvoicesCount", response.data.count);
            commit("updateInvoicesCancelSource", null);
            dispatch("fetchInvoicesStats");
            commit("updateInvoicesLoading", false);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }

            const error = utils.handleError(xhr_error);
            commit("updateInvoicesLoadingErrors", error.details);
            commit("updateInvoicesLoading", false);
            throw utils.handleError(xhr_error);
        }
    },

    async fetchInvoicesStats({ commit, dispatch, state, rootState }, params) {
        if (isCustomerInvoicesSectionKnownEmpty(rootState, state.section)) {
            commit('updateInvoicesStats', { ...EMPTY_INVOICES_STATS });
            commit('updateInvoicesStatsLoading', false);
            return;
        }

        commit("updateInvoicesStatsLoading", true);
        commit("updateInvoicesStatsLoadingErrors", null);

        if (state.invoices_stats_cancel_source) {
            state.invoices_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit("updateInvoicesStatsCancelSource", cancelSource);

        try {
            const response = await axios.get("/api/customerinvoices/stats/", {
                params: withSelectedIds(state.invoices_filters, state.selected_items),
                cancelToken: state.invoices_stats_cancel_source?.token,
            });
            commit("updateInvoicesStats", response.data);
            commit("updateInvoicesStatsCancelSource", null);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }

            const error = utils.handleError(xhr_error);
            commit("updateInvoicesStatsLoadingErrors", error.details);
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateInvoicesStatsLoading", false);
        }
    },

    async fetchBillingAccounts({ commit, dispatch, state }, params) {
        commit("updateBillingAccountsLoading", true);
        commit("updateBillingAccountsLoadingErrors", null);

        try {
            const response = await axios.get("/api/billingaccounts/");
            commit("updateBillingAccounts", response.data.results);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateBillingAccountsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateBillingAccountsLoading", false);
        }
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/customerinvoices/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                items: state.selected_items,
                recoverResult: params.action === "validate" && params.context
                    ? result => recoverCustomerInvoiceBulkPeriods(params.context, result, params.payload || {})
                    : undefined,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch('fetchInvoices');
            dispatch('session/fetchStats', null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },


    async deleteInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.delete(`/api/customerinvoices/${params.instance.id}/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },

};

const getters = {
    /**
     * Filters bound to Table UI: section constraints are hidden so tabs don't
     * surface forced values as user chips. API still uses invoices_filters.
     */
    invoices_table_filters(state) {
        const section = state.section || 'all';
        return omitFilterKeys(
            state.invoices_filters,
            getSectionConstraintKeys(getSectionFilters(section)),
        );
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
};
